// _elements/primitives/MockUpFrame.tsx
"use client";

import React, { useState } from "react";
import styled from "styled-components";
import type { MockupFrameBlock, MockupAnnotation } from "../../_sys/_types";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { getIcon } from "../blocks/shared";

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.large} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FrameShell = styled.div<{ $frame: "browser" | "desktopApp" | "none" }>`
  position: relative;
  width: 100%;
  max-width: 900px;
  border-radius: ${({ theme, $frame }) =>
    $frame === "none" ? theme.borders.radius.medium : theme.borders.radius.big};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.large};
  background: ${({ theme }) => theme.colors.static.black};
  border: ${({ theme }) => theme.borders.width.thin} solid
    ${({ theme }) => theme.colors.disabled};
`;

const ChromeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.disabled};
`;

const ChromeDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-block;
`;

const ChromeUrlPill = styled.span`
  margin-left: ${({ theme }) => theme.spacing.small};
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.static.grey};
`;

const ImageArea = styled.div<{ $broken: boolean }>`
  position: relative;
  width: 100%;
  /* Reserve a stable aspect ratio BEFORE the image loads (or if it fails)
     so the frame never collapses and annotation pins never get positioned
     inside a near-zero-height box. 16:10 is a reasonable app-screenshot
     default; real images simply fill it via object-fit. */
  aspect-ratio: 16 / 10;
  background: ${({ theme }) => theme.colors.static.black};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: ${({ $broken }) => ($broken ? "none" : "block")};
  }
`;

const BrokenImageFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.static.grey};
  font-family: ${({ theme }) => theme.typography.fonts.medium};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  background: ${({ theme }) => theme.colors.disabled};
`;

const AnnotationPin = styled.button<{ $x: number; $y: number }>`
  position: absolute;
  top: ${({ $y }) => $y}%;
  left: ${({ $x }) => $x}%;
  transform: translate(-50%, -50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.static.white};
  font-family: ${({ theme }) => theme.typography.fonts.bold};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.semitransparent};
  animation: majik-pin-pulse 2.4s ease-in-out infinite;

  @keyframes majik-pin-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.semitransparent};
    }
    50% {
      box-shadow: 0 0 0 9px transparent;
    }
  }
`;

const AnnotationTooltip = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  top: ${({ $y }) => $y}%;
  left: ${({ $x }) => $x}%;
  transform: translate(-50%, calc(-100% - 14px));
  max-width: 240px;
  padding: ${({ theme }) => theme.spacing.tight}
    ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  background: ${({ theme }) => theme.colors.static.black};
  color: ${({ theme }) => theme.colors.static.white};
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  line-height: ${({ theme }) => theme.typography.lineHeights.body};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  z-index: ${({ theme }) => theme.zIndex.tooltip};
  pointer-events: none;
`;

const Caption = styled.p`
  margin-top: ${({ theme }) => theme.spacing.small};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fonts.regularItalic};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 640px;
`;

export const MockUpFrame: React.FC<{ block: MockupFrameBlock }> = ({
  block,
}) => {
  const containerRef = useScrollReveal({ selector: "self", y: 40 });
  const [activeAnnotation, setActiveAnnotation] = useState<number | null>(null);
  const [broken, setBroken] = useState(false);
  const ImageIcon = getIcon("file");

  return (
    <Wrapper ref={containerRef}>
      <FrameShell $frame={block.frame}>
        {block.frame === "browser" && (
          <ChromeBar>
            <ChromeDot $color="#ff5f57" />
            <ChromeDot $color="#febc2e" />
            <ChromeDot $color="#28c840" />
            <ChromeUrlPill>{block.alt || "majikah.solutions"}</ChromeUrlPill>
          </ChromeBar>
        )}
        {block.frame === "desktopApp" && (
          <ChromeBar>
            <ChromeDot $color="#ff5f57" />
            <ChromeDot $color="#febc2e" />
            <ChromeDot $color="#28c840" />
          </ChromeBar>
        )}
        <ImageArea $broken={broken}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={block.image}
            alt={block.alt || "Product screenshot"}
            onError={() => setBroken(true)}
            onLoad={() => setBroken(false)}
          />
          {broken && (
            <BrokenImageFallback>
              <ImageIcon size={28} weight="light" />
              Screenshot unavailable
            </BrokenImageFallback>
          )}
          {block.annotations?.map((annotation: MockupAnnotation, idx) => (
            <React.Fragment key={idx}>
              <AnnotationPin
                type="button"
                $x={annotation.x}
                $y={annotation.y}
                onMouseEnter={() => setActiveAnnotation(idx)}
                onMouseLeave={() => setActiveAnnotation(null)}
                onFocus={() => setActiveAnnotation(idx)}
                onBlur={() => setActiveAnnotation(null)}
                aria-label={annotation.label}
              >
                {idx + 1}
              </AnnotationPin>
              {activeAnnotation === idx && (
                <AnnotationTooltip $x={annotation.x} $y={annotation.y}>
                  {annotation.label}
                </AnnotationTooltip>
              )}
            </React.Fragment>
          ))}
        </ImageArea>
      </FrameShell>
      {block.caption && <Caption>{block.caption}</Caption>}
    </Wrapper>
  );
};
